import { useEffect, useState } from 'react';
import { AuthenticationResult, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { useParams } from 'react-router-dom';

import { LoadingPage } from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { getTokenForaKS } from '../../redux/auth/authThunks';
import { basePath, envConfig } from '../../config';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { EmployerMyPagesErrorPage } from '../../components';

const hostName = `${window.location.protocol}//${window.location.host}`;

//Add redirectUri in  "Azure Portal" -> "Azure Active Directory" -> "App Registrations" ->
//"Individ" -> "Authentication" -> "Add URI" in Single-Page application area
const msalConfig = {
  auth: {
    authority: `https://login.microsoftonline.com/${envConfig.azureTenantId}`,
    clientId: envConfig.azureOrganizationClientId,
    redirectUri: `${hostName}${basePath}/login-fora-ks/callback/callback`,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: unknown, containsPii: unknown) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            console.log(message);
        }
      },
    },
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

export const AuthenticationForaKS = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(state => state.auth.loadingGetToken);
  const thunkError = useAppSelector(state => state.error.thunkError);
  const [msalInstanceInitialized, setMsalInstanceInitialized] = useState<boolean>(false);

  //Get partner id for incoming url from SalesForce
  const { partnerId } = useParams();

  const initMsalInstance = async () => {
    if (!msalInstanceInitialized) {
      await msalInstance.initialize();
      setMsalInstanceInitialized(true);
    }
  };

  useEffect(() => {
    initMsalInstance().then(() => {
      msalInstance
        .handleRedirectPromise()
        .then(async (response: AuthenticationResult | null) => {
          if (response === null) {
            //First response when trying to authenticate against the Azure AD
            msalInstance
              .loginRedirect({
                scopes: [],
              })
              .catch((error: unknown) => {
                console.error(error);
              });
          } else {
            //Authentication is done for the KS person. Use authenticated persons token from Azure Ad to get token from CERN
            dispatch(
              getTokenForaKS({
                partnerId: partnerId ?? '',
                azureJwtToken: response.idToken,
              })
            );
          }
        })
        .catch((error: unknown) => {
          console.error(error);
        });
    });
    // We don't need to add searchParams as a dependency here,
    // as they are defined before the react app is rendered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, partnerId]);

  return (
    <>
      {loading === ThunkRequestStatus.Pending && <LoadingPage animation="spinner" />}
      {/* In theory loading can be ThunkRequestStatus.Fulfilled but we don't have a valid accessToken.
      This would mean we are not properly authenticated and thus we should present an ErrorPage.
      This is why the condition is loading !== ThunkRequestStatus.Pending and not loading !== ThunkRequestStatus.Rejected */}
      {loading !== ThunkRequestStatus.Pending && (
        <EmployerMyPagesErrorPage type={thunkError?.code} technicalError={thunkError?.message} />
      )}
    </>
  );
};
